import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/hooks/useAuth";
import { useDashboards } from "@/hooks/useDashboards";
import { useIframeLoadTime } from "@/hooks/useIframeLoadTime";
import { useSessionTimer } from "@/hooks/useSessionTimer";
import { useTenant } from "@/hooks/useTenant";
import { useTrack } from "@/hooks/useTrack";
import {
  buildEmbedUrl,
  findDashboardBySlug,
  getOtherDashboards,
  isLookerReport,
  LOOKER_CANVAS_WIDTH_PX,
  type DashboardLike,
} from "@/lib/dashboardViewHelpers";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  useDocumentTitle,
} from "@simplicity/ui";
import { ChevronDown } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Navigate, useNavigate, useParams } from "react-router";
import { SignInRedirect } from "./Login";

/** Inner component rendered only after the dashboard is resolved. */
function DashboardContent({
  dashboard,
  allDashboards,
}: {
  dashboard: DashboardLike;
  allDashboards: DashboardLike[];
}) {
  const navigate = useNavigate();
  const { tenantPath } = useTenant();
  const track = useTrack();
  const { onSrcSet, onLoad } = useIframeLoadTime(dashboard.id, dashboard.slug, "full");

  useSessionTimer("dashboard_view", { dashboardId: dashboard.id, slug: dashboard.slug });

  const embedUrl = buildEmbedUrl(dashboard.url);
  const otherDashboards = getOtherDashboards(allDashboards, dashboard.id);
  // Only Looker's embed viewer scales its canvas to the box; Sheets and Slides
  // size themselves and keep the full-bleed treatment.
  const sizedToCanvas = isLookerReport(dashboard.url);

  useEffect(() => {
    onSrcSet();
  }, [embedUrl, onSrcSet]);

  // SIM-924: Home button now lives inside AppHeader (immediately after the
  // logo, with chevron separators between Logo › Home › Tenant › title).
  return (
    <div className="h-dvh bg-background flex flex-col">
      <AppHeader>
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-1.5 text-sm font-medium truncate hover:opacity-80 transition-opacity outline-hidden text-white">
            {dashboard.title}
            <ChevronDown className="h-4 w-4 shrink-0 opacity-70" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto w-72">
            {otherDashboards.map((d) => (
              <DropdownMenuItem
                key={d.id}
                onClick={() => {
                  track("dashboard_switch", { fromId: dashboard.id, toId: d.id });
                  void navigate(tenantPath(`/dashboard/${d.slug}`));
                }}
                className="cursor-pointer"
              >
                <span className="truncate">{d.title}</span>
              </DropdownMenuItem>
            ))}
            {otherDashboards.length === 0 && (
              <DropdownMenuItem disabled>No other dashboards</DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="text-xs flex gap-4 text-white/80">
          {dashboard.category && <span>Category: {dashboard.category}</span>}
          <span>School Year: {dashboard.school_year}</span>
        </span>
      </AppHeader>
      <div className={sizedToCanvas ? "flex-1 min-h-0 relative overflow-auto" : "flex-1 relative"}>
        <iframe
          src={embedUrl}
          className={
            sizedToCanvas
              ? "block mx-auto h-full border-0"
              : "absolute inset-0 w-full h-full border-0"
          }
          // A width in CSS pixels is the whole fix: `w-full` tracked the viewport,
          // which browser zoom shrinks by exactly the factor it magnifies, so the
          // two cancelled and the report never zoomed. Capped so a narrow window
          // re-fits instead of growing a horizontal scrollbar.
          style={sizedToCanvas ? { width: `min(${LOOKER_CANVAS_WIDTH_PX}px, 100%)` } : undefined}
          allowFullScreen
          onLoad={onLoad}
          // `allow-downloads` is load-bearing: without it the browser blocks every
          // download Looker starts — silently, with no page-JS error to report.
          // SIM-3219. DashboardView.spec.tsx pins the whole token set.
          sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads"
          title={`${dashboard.title} dashboard`}
        />
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <p className="text-muted-foreground text-sm">Loading...</p>
    </div>
  );
}

// An empty list also means the link's tenant isn't one of theirs, so this can't say which.
const NO_ACCESS_MESSAGE =
  "This dashboard doesn't exist, or you don't have access to it. Ask your school's portal admin to add you to a group that includes it.";

function DashboardBySlug() {
  const { slug } = useParams<{ slug: string }>();
  const { data: allDashboards = [], isLoading } = useDashboards();
  const { tenantPath } = useTenant();

  const dashboard = useMemo(() => findDashboardBySlug(allDashboards, slug), [allDashboards, slug]);

  useDocumentTitle(dashboard?.title ?? null, { suffix: " | SimplySpark" });

  if (isLoading) return <LoadingState />;

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <p className="max-w-md text-center text-sm text-muted-foreground">{NO_ACCESS_MESSAGE}</p>
      </div>
    );
  }

  // Spark dashboards render via dedicated route
  if (dashboard.type === "spark" && dashboard.sparkDashboardId) {
    return <Navigate to={tenantPath(`/spark/${dashboard.sparkDashboardId}`)} replace />;
  }

  return <DashboardContent dashboard={dashboard} allDashboards={allDashboards} />;
}

// The dashboards query stays disabled until a user and tenant exist, so resolving
// the slug any earlier would report a real dashboard as not found.
const DashboardView = () => {
  const { user, loading: authLoading } = useAuth();
  const { isLoading: tenantsLoading } = useTenant();

  if (!authLoading && !user) return <SignInRedirect />;
  if (authLoading || tenantsLoading) return <LoadingState />;

  return <DashboardBySlug />;
};

export default DashboardView;
